import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Form, Button, Card, Row, Col, Spinner } from 'react-bootstrap';
import { ToastContainer } from 'react-toastify';
import { HelpCircle } from 'lucide-react';
import { useSessionMessage } from './hooks/useSessionMessage';
import axios from '../../axiosConfig';
import useTenantOnRoad from './hooks/useTenantOnRoad';
import 'react-toastify/dist/ReactToastify.css';

const ValidarConductor = () => {
	const [numeroIdentificacion, setNumeroIdentificacion] = useState('');
	const [numeroManifiesto, setManifiesto] = useState('');
	const [error, setError] = useState('');
	const [loading, setLoading] = useState(false);

	const navigate = useNavigate(); 

    // Hook para manejar mensajes de sesión
    useSessionMessage();

    // Obtener el tenant de los params
    const { tenantEmpresa } = useParams();
    
    // Hook para cargar datos de la empresa
    const { loadingEmpresa, error: errorEmpresa } = useTenantOnRoad(tenantEmpresa);

	useEffect(() => {
        // Verificar si viene de una salida manual
        const esSalidaManual = sessionStorage.getItem('salida_manual');
        
        if (esSalidaManual) {
            sessionStorage.removeItem('salida_manual');
            // No redirigir automáticamente, mostrar el formulario
            return;
        }

        // Si hay token, redirigir a principal
        const tokenOperacion = localStorage.getItem('tokenOperacion');
        if (tokenOperacion) {
            navigate('/principalonroad', { replace: true });
        }
    }, [navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        
        // Validación básica
        if (!numeroIdentificacion.trim() || !numeroManifiesto.trim()) {
            setError('Por favor complete todos los campos');
            setLoading(false);
            return;
        }
        try {
            localStorage.clear();
            const response = await axios.get('/validarConductor', {
                params: {
                    identificacion: numeroIdentificacion.trim(),
                    manifiesto: numeroManifiesto.trim(),
                    tenantEmpresa: tenantEmpresa
                },
                validateStatus: (status) => {
                    return status >= 200 && status < 500;
                }
            });

            if (response.data && response.data.tokenOperacion) {
                // Guardar datos en localStorage
                localStorage.setItem('tokenOperacion', response.data.tokenOperacion);

                // Datos de la empresa
                localStorage.setItem('idEmpresaCodigo', response.data.operacion.id_empresa_codigo);
                localStorage.setItem('nombreEmpresa', response.data.operacion.nombre_empresa);
                localStorage.setItem('tenantEmpresa', tenantEmpresa);

                // Datos del manifiesto
                localStorage.setItem('manifiesto', response.data.operacion.manifiesto);
                localStorage.setItem('ruta', response.data.operacion.ruta_viaje);

                // Datos del conductor 
                localStorage.setItem('idConductor', response.data.operacion.conductor);
                localStorage.setItem('nombreConductor', response.data.operacion.nombre_conductor);

                // Dato del vehículo
                localStorage.setItem('placa', response.data.operacion.placa);

                navigate('/principalonroad');
            } else {
                const { message, errors } = response.data;
                let errorDetails = message;
                if (errors) {
                    Object.keys(errors).forEach((key) => {
                        errorDetails += `\n${key}: ${errors[key].join(', ')}`;
                    });
                }
                throw new Error(errorDetails || 'Error desconocido');
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleHelpClick = () => {
        window.open('https://youtu.be/o9BKyovYqVc?si=k1Mx3k_bHiWRJAwD', '_blank', 'noopener,noreferrer');
    };

    // Mostrar loading mientras carga la empresa
    if (loadingEmpresa) {
        return (
            <Container className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: '70vh' }}>
                <div className="text-center">
                    <Spinner animation="border" role="status" className="mb-3">
                        <span className="visually-hidden">Cargando...</span>
                    </Spinner>
                    <p className="text-muted">Validando empresa...</p>
                </div>
            </Container>
        );
    }

    // Mostrar error si la empresa no existe
    if (errorEmpresa) {
        return (
            <Container className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: '70vh' }}>
                <Card className="shadow text-center" style={{ width: '100%', maxWidth: '500px', padding: '20px' }}>
                    <Card.Body>
                        <div className="mb-4">
                            <span className="material-icons text-danger" style={{ fontSize: '4rem' }}>
                                apartment
                            </span>
                        </div>
                        <h2 className="text-danger mb-3">Empresa No Encontrada</h2>
                    </Card.Body>
                </Card>
            </Container>
        );
    }

    // Mostrar formulario de validación si la empresa existe
    return (
        <Container className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: '70vh' }}>
            <Card className="shadow" style={{ width: '100%', maxWidth: '500px', padding: '20px' }}>
                {/* Botón de ayuda */}
                <div 
                    onClick={handleHelpClick}
                    style={{
                        position: 'absolute',
                        top: '15px',
                        right: '15px',
                        cursor: 'pointer',
                        zIndex: 10
                    }}
                    title="Ver tutorial de ayuda"
                    role="button"
                    tabIndex={0}
                >
                    <HelpCircle 
                        size={24} 
                        color="#0d6efd"
                        strokeWidth={1.6}
                        style={{
                            transition: 'transform 0.2s ease, color 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'scale(1.15)';
                            e.currentTarget.style.color = '#0a58ca';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.style.color = '#0d6efd';
                        }}
                    />
                </div>
                <Row className="w-100 justify-content-center mb-4">
                    <Col xs={12} className="d-flex justify-content-center">
                        <img src="/logosafite2.png" alt="Logo" width={200} className="logo-animated" />
                    </Col>
                </Row>
                
                <h2 className="text-center mb-3">Ingresar OnRoad</h2>

                <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3" controlId="formTitle">
                        <Form.Label>Nro. Identificación</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Ingrese número de identificación"
                            value={numeroIdentificacion}
                            onChange={(e) => setNumeroIdentificacion(e.target.value)}
                            required
                        />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="formTitle">
                        <Form.Label>Nro. Manifiesto</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Ingrese número de manifiesto"
                            value={numeroManifiesto}
                            onChange={(e) => setManifiesto(e.target.value)}
                            required
                        />
                    </Form.Group>

                    {error && <p className="text-danger">{error}</p>}

                    <Button variant="primary" type="submit" className="w-100" disabled={loading}>
                        {loading ? 'Validando...' : (
                            <>
                                <span className="material-icons" style={{ verticalAlign: 'middle' }}>login</span> Ingresar
                            </>
                        )}
                    </Button>
                </Form>
            </Card>
            <ToastContainer 
                position="top-center" 
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
            />
        </Container>
    );
};

export default ValidarConductor;